import { Link } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Wand2, ArrowLeft } from "lucide-react";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border shadow-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/" className="flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Link>
              </Button>
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                <Wand2 className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-primary">Privacy Policy</h1>
                <p className="text-sm text-muted-foreground">How we protect your data</p>
              </div>
            </div>
            <Navigation />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <div className="mb-8">
            <p className="text-muted-foreground">Last updated: January 19, 2025</p>
          </div>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-bold mb-4">Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                At AI Humanizer Text ("we," "our," or "us"), we respect your privacy and are committed to protecting your personal data. 
                This privacy policy explains how we collect, use, and safeguard your information when you use our text humanization service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Information We Collect</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold mb-2">Account Information</h3>
                  <p className="text-muted-foreground">
                    When you create an account, we collect your email address, full name, and encrypted password.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Usage Data</h3>
                  <p className="text-muted-foreground">
                    We collect information about how you use our service, including the text you submit for humanization, 
                    processing preferences, and usage patterns to improve our service.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Technical Data</h3>
                  <p className="text-muted-foreground">
                    We automatically collect technical information such as your IP address, browser type, 
                    device information, and access logs for security and performance purposes.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">How We Use Your Information</h2>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>To provide and improve our text humanization services</li>
                <li>To manage your account and process payments</li>
                <li>To communicate with you about service updates and support</li>
                <li>To ensure the security and integrity of our platform</li>
                <li>To analyze usage patterns and improve our algorithms</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Data Security</h2>
              <p className="text-muted-foreground leading-relaxed">
                We implement industry-standard security measures to protect your personal information and text data. 
                Your submitted text is processed securely and is not permanently stored on our servers after processing. 
                All data transmission is encrypted using SSL/TLS protocols.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Data Sharing</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>With trusted service providers who help us operate our platform (under strict confidentiality agreements)</li>
                <li>When required by law or legal process</li>
                <li>To protect our rights, property, or safety, or that of our users</li>
                <li>In connection with a business transfer or merger (with prior notice)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Your Rights</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You have the following rights regarding your personal data:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Access and review your personal information</li>
                <li>Correct inaccurate or incomplete data</li>
                <li>Delete your account and associated data</li>
                <li>Export your data in a portable format</li>
                <li>Opt-out of marketing communications</li>
                <li>Object to certain processing activities</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Cookies and Tracking</h2>
              <p className="text-muted-foreground leading-relaxed">
                We use essential cookies to provide our service functionality and analytics cookies to understand usage patterns. 
                You can control cookie settings through your browser preferences, though some features may not work properly 
                if essential cookies are disabled.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Children's Privacy</h2>
              <p className="text-muted-foreground leading-relaxed">
                Our service is not intended for children under 13 years of age. We do not knowingly collect personal information 
                from children under 13. If we discover we have collected such information, we will delete it promptly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Changes to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this privacy policy from time to time. We will notify you of significant changes by email 
                or through our service. Your continued use of our service after such changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have questions about this privacy policy or our data practices, please contact us at:
              </p>
              <div className="bg-muted p-4 rounded-lg mt-4">
                <p className="font-medium">Email: privacy@aihumanizertext.com</p>
                <p className="text-sm text-muted-foreground mt-2">
                  We will respond to privacy-related inquiries within 30 days.
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Privacy;